// src/components/MenuLateral.js
import React, { useEffect, useMemo, useState } from "react";
import NoreaElfico from "../assets/images/norea-elfico.png";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlug,
  faTachometerAlt,
  faUsers,
  faTasks,
  faChartBar,
  faCog,
  faQuestionCircle,
  faSignOutAlt,
  faDollarSign,
  faTags,
  faBrain,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp, faMeta, faGoogle } from "@fortawesome/free-brands-svg-icons";

import { MonitorCog, Menu as MenuIcon, X as XIcon } from "lucide-react";
import styled, { createGlobalStyle } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";

const SIDEBAR_EXPANDED = 250;
const SIDEBAR_COLLAPSED = 72;
const MOBILE_BP = 768;

/**
 * Ajustes globais:
 * - desktop: empurra main-content pelo sidebar colapsado e no hover expandido
 * - mobile: não empurra (main-content sempre full), remove padding inline do App
 */
const GlobalAdapt = createGlobalStyle`
  .main-content {
    margin-left: 0;
    transition: margin-left 180ms ease;
  }

  .has-sidebar .main-content {
    margin-left: ${SIDEBAR_COLLAPSED}px;
  }

  .has-sidebar .app-sidebar:hover ~ .main-content {
    margin-left: ${SIDEBAR_EXPANDED}px;
  }

  @media (max-width: ${MOBILE_BP}px){
    .has-sidebar .main-content{
      margin-left: 0 !important;
      padding: 0 !important;
    }
  }
`;

const Sidebar = styled.aside.attrs({ className: "app-sidebar" })`
  width: ${SIDEBAR_COLLAPSED}px;
  height: 100vh;
  background-color: #232332;
  color: #fff;
  display: flex;
  flex-direction: column;
  padding: 20px 16px;
  position: fixed;
  left: 0;
  top: 0;
  transition: width 180ms ease, transform 180ms ease;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  z-index: 100;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 5px;
    height: 100%;
    background-color: #2f2f41;
  }

  /* Desktop: expandir no hover */
  &:hover {
    width: ${SIDEBAR_EXPANDED}px;
  }

  /* ===== MOBILE: vira drawer ===== */
  @media (max-width: ${MOBILE_BP}px) {
    z-index: 10001;
    width: ${SIDEBAR_EXPANDED}px;
    transform: translateX(-110%);
    padding-top: 16px;

    /* IMPORTANTE: permitir scroll do menu no mobile */
    overflow-y: auto;
    overflow-x: hidden;

    &.is-open {
      transform: translateX(0);
    }
  }
`;

/** Botões “imunes” a herança de CSS (hover, background, etc.) */
const CleanIconButton = styled.button`
  all: unset;
  box-sizing: border-box;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:active,
  &:focus {
    background: transparent !important;
    box-shadow: none !important;
    transform: none !important;
    filter: none !important;
    outline: none !important;
  }
`;

const MobileHamburger = styled(CleanIconButton)`
  display: none;

  @media (max-width: ${MOBILE_BP}px) {
    display: inline-flex;
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 10002;

    width: 42px;
    height: 42px;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #2f2f41;
    color: #fff;
    box-shadow:0 10px 20px rgba(0,0,0,.35);
  }
`;

const MobileClose = styled(CleanIconButton)`
  display: none;

  @media (max-width: ${MOBILE_BP}px) {
    display: inline-flex;
    position: absolute;
    top: 10px;
    right: 10px;

    width: 40px;
    height: 40px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
    z-index: 2;
  }
`;

const MobileOverlay = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BP}px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 10000;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 6px 0 16px;
  position: relative;
`;

const Logo = styled.img`
  width: 40px;
  margin: 8px 0;
  transition: width 180ms ease, margin 180ms ease;

  ${Sidebar}:hover & {
    width: 70px;
    margin: 30px 0 10px 10px;
  }

  @media (max-width: ${MOBILE_BP}px) {
    margin: 8px 0;
  }
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 10px;
  cursor: pointer;
  color: ${(props) => (props.$expanded ? "#FFD700" : "#FFF")};
  background: ${(props) => (props.$expanded ? "#2f2f41" : "transparent")};
  transition: background-color 0.2s, color 0.2s, gap 180ms ease;
  border-radius: 8px;
  white-space: nowrap;
  justify-content: center;

  &:hover {
    background-color: #2f2f41;
  }

  ${Sidebar}:hover & {
    gap: 10px;
    justify-content: flex-start;
  }

  @media (max-width: ${MOBILE_BP}px) {
    gap: 10px;
    justify-content: flex-start;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  font-size: 18px;
`;

const MenuTitle = styled.span`
  display: none;
  font-size: 16px;
  color: #fff;

  ${Sidebar}:hover & {
    display: inline;
  }

  @media (max-width: ${MOBILE_BP}px) {
    display: inline;
  }
`;

const SubMenu = styled.div`
  margin-left: 20px;
  display: none;
  transition: all 0.2s;

  a {
    color: #fff !important;
    text-decoration: none !important;
    font-size: 14px;
  }
  a:hover {
    color: #ffd700 !important;
  }

  ${Sidebar}:hover & {
    display: ${(props) => (props.$show ? "block" : "none")};
  }

  @media (max-width: ${MOBILE_BP}px) {
    display: ${(props) => (props.$show ? "block" : "none")};
  }
`;

function DraggableHamburger({ onOpen }) {
  const KEY = "norea_hamburger_pos";
  const DEFAULT = { x: 12, y: 12 };

  const [pos, setPos] = React.useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : DEFAULT;
    } catch {
      return DEFAULT;
    }
  });

  const draggingRef = React.useRef(false);
  const startRef = React.useRef({ x: 0, y: 0, px: 0, py: 0 });
  const movedRef = React.useRef(false);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const save = (p) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(p));
    } catch {}
  };

  const onPointerDown = (e) => {
    // só mobile
    if (window.innerWidth > MOBILE_BP) return;

    draggingRef.current = true;
    movedRef.current = false;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    startRef.current = { x: clientX, y: clientY, px: pos.x, py: pos.y };
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const dx = clientX - startRef.current.x;
    const dy = clientY - startRef.current.y;

    // se mexeu mais que um pouquinho, considera “drag”
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) movedRef.current = true;

    const btnSize = 42; // mesmo size do seu botão
    const maxX = window.innerWidth - btnSize - 6;
    const maxY = window.innerHeight - btnSize - 6;

    const next = {
      x: clamp(startRef.current.px + dx, 6, maxX),
      y: clamp(startRef.current.py + dy, 6, maxY),
    };

    setPos(next);

    // impede o scroll enquanto arrasta
    if (e.cancelable) e.preventDefault();
  };

  const onPointerUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    save(pos);
  };

  // listeners globais enquanto arrasta (touch/mouse)
  React.useEffect(() => {
    const move = (e) => onPointerMove(e);
    const up = () => onPointerUp();

    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos]);

  const handleClick = () => {
    // se foi drag, não abre menu
    if (movedRef.current) return;
    onOpen?.();
  };

  return (
    <MobileHamburger
      type="button"
      onClick={handleClick}
      onMouseDown={onPointerDown}
      onTouchStart={onPointerDown}
      aria-label="Abrir menu"
      style={{
        left: pos.x,
        top: pos.y,
        right: "auto",
        bottom: "auto",
      }}
    >
      <MenuIcon size={22} />
    </MobileHamburger>
  );
}

function MenuLateral({ handleLogout }) {
  const [activeMenu, setActiveMenu] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [usuarioId, setUsuarioId] = useState(null);
  const [whatsappAtivo, setWhatsappAtivo] = useState(false);
  const [iaAtiva, setIaAtiva] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = () => window.innerWidth <= MOBILE_BP;

  const go = (path) => {
    navigate(path);
    // fecha o drawer apenas quando navegar (submenu / item final)
    if (isMobile()) setMobileOpen(false);
  };

  useEffect(() => {
    const dataStr = localStorage.getItem("usuario");
    if (!dataStr) return;

    try {
      const data = JSON.parse(dataStr);
      setWhatsappAtivo(data.whatsapp_ativo === 1);
      setIaAtiva(data.ia_ativa === 1);
      setUsuarioId(Number(data.id));
      setIsAdmin(data.tipo_usuario === "master");
    } catch {}
  }, []);

  // No mobile: clique no menu principal só abre/fecha submenu (não fecha drawer)
  const toggleMenu = (menu) => {
    setActiveMenu((prev) => (prev === menu ? "" : menu));
  };

  // Fecha o drawer se trocar de tamanho (mobile->desktop)
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > MOBILE_BP) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const showUsersAdmin = useMemo(() => isAdmin && usuarioId !== 80, [isAdmin, usuarioId]);

  // Helpers de "ativo" por rota
  const pathIs = (p) => location.pathname === p;
  const pathStarts = (p) => location.pathname.startsWith(p);

  const leadsActive =
    activeMenu === "leads" || pathIs("/cadastro-lead") || pathIs("/listagem-leads");

  const actionsActive =
    activeMenu === "actions" || pathIs("/criar-acao") || pathIs("/acoes");

  const tagsActive = activeMenu === "tags" || pathIs("/gerenciar-tags");

  const usersActive =
    activeMenu === "users" ||
    pathIs("/gerenciar-usuarios") ||
    pathIs("/adicionar-usuario") ||
    pathIs("/usuario-config") ||
    pathIs("/openai/iarecomendacoes-meta");

  const financeiroActive =
    activeMenu === "financeiro" ||
    pathIs("/wzp/painel-mensagens-teste") ||
    pathStarts("/financeiro/") ||
    pathIs("/listar-cobrancas");

  const configWzpActive =
    activeMenu === "config-wzp" || pathIs("/wzp/painel-instancias");

  const metaAdmActive =
    activeMenu === "meta-adm" ||
    pathIs("/meta/vincular-contas-meta") ||
    pathIs("/googleads/integracao-googleads");

  const metaUserActive =
    activeMenu === "meta-user" ||
    pathIs("/meta/relatorio-meta") ||
    pathIs("/meta/saldo-meta");

  const googleAdmActive =
    activeMenu === "google-adm" || pathIs("/googleads/relatorio-googleads");

const whatsappIsRoute =
  pathIs("/wzp/painel-mensagens") ||
  pathIs("/wzp/respostas-rapidas") ||
  pathIs("/wzp/fila-mensagens") ||
  pathIs("/wzp/painel-whatsapp") ||
  pathStarts("/funil/");
const whatsappExpanded = activeMenu === "whatsapp";
const whatsappMenuActive = whatsappIsRoute || whatsappExpanded; // só para cor amarela

  const openaiActive =
    activeMenu === "openai" || pathStarts("/openai/");

  const reportsActive =
    activeMenu === "reports" || pathIs("/relatorios") || pathIs("/exportacao-dados");

  const settingsActive =
    activeMenu === "settings" || pathIs("/alterar-senha") || pathIs("/meus-dados");

  const helpActive =
    activeMenu === "help" || pathIs("/ajuda-suporte") || pathIs("/opiniao");

  return (
    <>
      <GlobalAdapt />

      {/* Botão hambúrguer (somente mobile) */}
     <DraggableHamburger onOpen={() => setMobileOpen(true)} />

      {mobileOpen && <MobileOverlay onClick={() => setMobileOpen(false)} />}

      <Sidebar className={mobileOpen ? "is-open" : ""}>
        {/* botão fechar (somente mobile) */}
        <MobileClose
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
        >
          <XIcon size={20} />
        </MobileClose>

        <TopRow>
          <Logo src={NoreaElfico} alt="Norea Logo" />
        </TopRow>

        {/* Dashboard */}
        <MenuItem onClick={() => go("/dashboard")} $expanded={pathIs("/dashboard")}>
          <Icon icon={faTachometerAlt} />
          <MenuTitle>Dashboard</MenuTitle>
        </MenuItem>

        {/* Leads */}
        <MenuItem onClick={() => toggleMenu("leads")} $expanded={leadsActive}>
          <Icon icon={faTasks} />
          <MenuTitle>Leads</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "leads" || leadsActive}>
          <MenuItem onClick={() => go("/cadastro-lead")} $expanded={pathIs("/cadastro-lead")}>
            <span>Cadastrar Lead</span>
          </MenuItem>
          <MenuItem onClick={() => go("/listagem-leads")} $expanded={pathIs("/listagem-leads")}>
            <span>Listagem de Leads</span>
          </MenuItem>
        </SubMenu>

        {/* Ações */}
        <MenuItem onClick={() => toggleMenu("actions")} $expanded={actionsActive}>
          <Icon icon={faTasks} />
          <MenuTitle>Ações</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "actions" || actionsActive}>
          <MenuItem onClick={() => go("/criar-acao")} $expanded={pathIs("/criar-acao")}>
            <span>Cadastrar Ação</span>
          </MenuItem>
          <MenuItem onClick={() => go("/acoes")} $expanded={pathIs("/acoes")}>
            <span>Listagem de Ações</span>
          </MenuItem>
        </SubMenu>

        {/* Tags */}
        <MenuItem onClick={() => toggleMenu("tags")} $expanded={tagsActive}>
          <Icon icon={faTags} />
          <MenuTitle>Tags</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "tags" || tagsActive}>
          <MenuItem onClick={() => go("/gerenciar-tags")} $expanded={pathIs("/gerenciar-tags")}>
            <span>Gerenciar Tags</span>
          </MenuItem>
        </SubMenu>

        {/* Usuários (Admin) */}
        {showUsersAdmin && (
          <>
            <MenuItem onClick={() => toggleMenu("users")} $expanded={usersActive}>
              <Icon icon={faUsers} />
              <MenuTitle>Usuários</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "users" || usersActive}>
              <MenuItem onClick={() => go("/gerenciar-usuarios")} $expanded={pathIs("/gerenciar-usuarios")}>
                <span>Lista de Usuários</span>
              </MenuItem>
              <MenuItem onClick={() => go("/adicionar-usuario")} $expanded={pathIs("/adicionar-usuario")}>
                <span>Criar Usuário</span>
              </MenuItem>
              <MenuItem onClick={() => go("/usuario-config")} $expanded={pathIs("/usuario-config")}>
                <span>Configurações de Usuário</span>
              </MenuItem>
              <MenuItem onClick={() => go("/openai/iarecomendacoes-meta")} $expanded={pathIs("/openai/iarecomendacoes-meta")}>
                <span>Recomendações Meta</span>
              </MenuItem>
            </SubMenu>

            {/* Financeiro */}
            <MenuItem onClick={() => toggleMenu("financeiro")} $expanded={financeiroActive}>
              <Icon icon={faDollarSign} />
              <MenuTitle>Financeiro</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "financeiro" || financeiroActive}>
              <MenuItem onClick={() => go("/wzp/painel-mensagens-teste")} $expanded={pathIs("/wzp/painel-mensagens-teste")}>
                <span>TESTE</span>
              </MenuItem>
              <MenuItem onClick={() => go("/financeiro/calculo-comissoes")} $expanded={pathIs("/financeiro/calculo-comissoes")}>
                <span>Comissões</span>
              </MenuItem>
              <MenuItem onClick={() => go("/listar-cobrancas")} $expanded={pathIs("/listar-cobrancas")}>
                <span>Listar Cobranças</span>
              </MenuItem>
              <MenuItem onClick={() => go("/financeiro/listar-data-cobranca")} $expanded={pathIs("/financeiro/listar-data-cobranca")}>
                <span>Data Cobrança</span>
              </MenuItem>
              <MenuItem onClick={() => go("/financeiro/associar-pagamento")} $expanded={pathIs("/financeiro/associar-pagamento")}>
                <span>Associação Pendentes</span>
              </MenuItem>
              <MenuItem onClick={() => go("/financeiro/previsao-recebimentos")} $expanded={pathIs("/financeiro/previsao-recebimentos")}>
                <span>Previsão</span>
              </MenuItem>
              <MenuItem onClick={() => go("/financeiro/notas")} $expanded={pathIs("/financeiro/notas")}>
                <span>Notas Fiscais</span>
              </MenuItem>
              <MenuItem onClick={() => go("/financeiro/saldo-asaas")} $expanded={pathIs("/financeiro/saldo-asaas")}>
                <span>Saldo</span>
              </MenuItem>
            </SubMenu>

            {/* Configurar WhatsApp */}
            <MenuItem onClick={() => toggleMenu("config-wzp")} $expanded={configWzpActive}>
              <Icon icon={faPlug} />
              <MenuTitle>Configurar WhatsApp</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "config-wzp" || configWzpActive}>
              <MenuItem onClick={() => go("/wzp/painel-instancias")} $expanded={pathIs("/wzp/painel-instancias")}>
                <span>Painel Instâncias</span>
              </MenuItem>
            </SubMenu>

            {/* Ads Admin */}
            <MenuItem onClick={() => toggleMenu("meta-adm")} $expanded={metaAdmActive}>
              <span style={{ width: 18, display: "inline-flex", justifyContent: "center" }}>
                <MonitorCog size={20} color="white" />
              </span>
              <MenuTitle>Config Tráfego</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "meta-adm" || metaAdmActive}>
              <MenuItem onClick={() => go("/meta/vincular-contas-meta")} $expanded={pathIs("/meta/vincular-contas-meta")}>
                <span>Meta</span>
              </MenuItem>
              <MenuItem onClick={() => go("/googleads/integracao-googleads")} $expanded={pathIs("/googleads/integracao-googleads")}>
                <span>Google Ads</span>
              </MenuItem>
            </SubMenu>
          </>
        )}

        {/* Meta Usuário */}
        <MenuItem onClick={() => toggleMenu("meta-user")} $expanded={metaUserActive}>
          <Icon icon={faMeta} />
          <MenuTitle>Meta Ads</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "meta-user" || metaUserActive}>
          <MenuItem onClick={() => go("/meta/relatorio-meta")} $expanded={pathIs("/meta/relatorio-meta")}>
            <span>Relatórios</span>
          </MenuItem>
          <MenuItem onClick={() => go("/meta/saldo-meta")} $expanded={pathIs("/meta/saldo-meta")}>
            <span>Saldo</span>
          </MenuItem>
        </SubMenu>

        {/* Google Ads */}
        <MenuItem onClick={() => toggleMenu("google-adm")} $expanded={googleAdmActive}>
          <Icon icon={faGoogle} />
          <MenuTitle>Google Ads</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "google-adm" || googleAdmActive}>
          <MenuItem onClick={() => go("/googleads/relatorio-googleads")} $expanded={pathIs("/googleads/relatorio-googleads")}>
            <span>Relatório</span>
          </MenuItem>
        </SubMenu>

        {/* WhatsApp */}
        {whatsappAtivo && (
          <>
            <MenuItem onClick={() => toggleMenu("whatsapp")} $expanded={whatsappMenuActive}>
            <Icon icon={faWhatsapp} />
            <MenuTitle>WhatsApp</MenuTitle>
          </MenuItem>
            <SubMenu $show={whatsappExpanded}>
              <MenuItem onClick={() => go("/wzp/painel-mensagens")} $expanded={pathIs("/wzp/painel-mensagens")}>
                <span>Mensagens</span>
              </MenuItem>
              <MenuItem onClick={() => go("/wzp/respostas-rapidas")} $expanded={pathIs("/wzp/respostas-rapidas")}>
                <span>Respostas Rápidas</span>
              </MenuItem>
              <MenuItem onClick={() => go("/wzp/fila-mensagens")} $expanded={pathIs("/wzp/fila-mensagens")}>
                <span>Fila de Mensagens</span>
              </MenuItem>
              <MenuItem onClick={() => go("/wzp/painel-whatsapp")} $expanded={pathIs("/wzp/painel-whatsapp")}>
                <span>Conectar</span>
              </MenuItem>
              <MenuItem onClick={() => go("/funil/painel-funil")} $expanded={pathIs("/funil/painel-funil")}>
                <span>Funil WhatsApp</span>
              </MenuItem>
            </SubMenu>
          </>
        )}

        {/* OpenAI (IA) */}
        {iaAtiva && (
          <>
            <MenuItem onClick={() => toggleMenu("openai")} $expanded={openaiActive}>
              <Icon icon={faBrain} />
              <MenuTitle>OpenAI</MenuTitle>
            </MenuItem>
            <SubMenu $show={activeMenu === "openai" || openaiActive}>
              <MenuItem onClick={() => go("/openai/painel")} $expanded={pathIs("/openai/painel")}>
                <span>Painel da IA</span>
              </MenuItem>
            </SubMenu>
          </>
        )}

        {/* Dados */}
        <MenuItem onClick={() => toggleMenu("reports")} $expanded={reportsActive}>
          <Icon icon={faChartBar} />
          <MenuTitle>Dados</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "reports" || reportsActive}>
          <MenuItem onClick={() => go("/relatorios")} $expanded={pathIs("/relatorios")}>
            <span>Relatórios</span>
          </MenuItem>
          <MenuItem onClick={() => go("/exportacao-dados")} $expanded={pathIs("/exportacao-dados")}>
            <span>Exportar Dados</span>
          </MenuItem>
        </SubMenu>

        {/* Configurações */}
        <MenuItem onClick={() => toggleMenu("settings")} $expanded={settingsActive}>
          <Icon icon={faCog} />
          <MenuTitle>Configurações</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "settings" || settingsActive}>
          <MenuItem onClick={() => go("/alterar-senha")} $expanded={pathIs("/alterar-senha")}>
            <span>Alterar Senha</span>
          </MenuItem>
          <MenuItem onClick={() => go("/meus-dados")} $expanded={pathIs("/meus-dados")}>
            <span>Meus Dados</span>
          </MenuItem>
        </SubMenu>

        {/* Ajuda */}
        <MenuItem onClick={() => toggleMenu("help")} $expanded={helpActive}>
          <Icon icon={faQuestionCircle} />
          <MenuTitle>Ajuda</MenuTitle>
        </MenuItem>
        <SubMenu $show={activeMenu === "help" || helpActive}>
          <MenuItem onClick={() => go("/ajuda-suporte")} $expanded={pathIs("/ajuda-suporte")}>
            <span>Ajuda e Suporte</span>
          </MenuItem>
          <MenuItem onClick={() => go("/opiniao")} $expanded={pathIs("/opiniao")}>
            <span>Opinião do Usuário</span>
          </MenuItem>
        </SubMenu>

        {/* Logout */}
        <MenuItem
          onClick={() => {
            if (isMobile()) setMobileOpen(false);
            handleLogout?.();
          }}
        >
          <Icon icon={faSignOutAlt} />
          <MenuTitle>Sair</MenuTitle>
        </MenuItem>
      </Sidebar>
    </>
  );
}

export default MenuLateral;